import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  AutoAwesome,
  Delete,
  Add,
  ArrowBack,
  PictureAsPdf,
} from '@mui/icons-material';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { parseGPTResponse, mergePageResults } from '../../utils/cvMenuParser';

import { ENDPOINTS } from '../../config';

const CV_ENDPOINT = ENDPOINTS.cvUploadFile;

/**
 * Parse a comma-separated dietary tag string into boolean fields matching
 * the DishDetails schema (dishGF, dishDF, dishNF, dishV, dishVG, etc.).
 */
const parseDietaryTags = (dietaryStr) => {
  const tags = (dietaryStr || '')
    .split(/[,/]+/)
    .map((t) => t.trim().toUpperCase());

  return {
    dishGF: tags.some((t) => t === 'GF' || t === 'GLUTEN FREE' || t === 'GLUTEN-FREE'),
    dishDF: tags.some((t) => t === 'DF' || t === 'DAIRY FREE' || t === 'DAIRY-FREE'),
    dishNF: tags.some((t) => t === 'NF' || t === 'NUT FREE' || t === 'NUT-FREE'),
    dishV: tags.some((t) => t === 'V' || t === 'VEGETARIAN'),
    dishVG: tags.some((t) => t === 'VG' || t === 'VEGAN'),
    dishLGI: tags.some((t) => t === 'LGI' || t === 'LOW GI'),
    dishGFO: tags.some((t) => t === 'GFO' || t === 'GLUTEN FREE OPTION' || t === 'GLUTEN-FREE OPTION'),
    dishDFO: tags.some((t) => t === 'DFO' || t === 'DAIRY FREE OPTION' || t === 'DAIRY-FREE OPTION'),
    dishNFO: tags.some((t) => t === 'NFO' || t === 'NUT FREE OPTION' || t === 'NUT-FREE OPTION'),
    dishVGO: tags.some((t) => t === 'VGO' || t === 'VEGAN OPTION'),
  };
};

/**
 * AdminMenuCVImport
 *
 * Dialog that lets a Piatto admin scan a stored venue image/PDF (or upload
 * a new file) through the existing GPT-4o CV endpoint, review the extracted
 * menu data, and save it directly to Firestore under the venue's bizId.
 *
 * Props:
 *   venue      – Business document object (must have .id, .bizName, .images?)
 *   open       – boolean
 *   onClose    – () => void
 *   onImported – optional callback fired after successful save
 */
const AdminMenuCVImport = ({ venue, open, onClose, onImported }) => {
  const [step, setStep] = useState(0); // 0: source  1: analysing  2: review
  const [uploadFile, setUploadFile] = useState(null);
  const [scrapedFiles, setScrapedFiles] = useState([]);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const [error, setError] = useState('');
  const [menuName, setMenuName] = useState('');
  const [menuType, setMenuType] = useState('Food');
  const [menuDesc, setMenuDesc] = useState('');
  const [dishes, setDishes] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const reset = () => {
    setStep(0);
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    setUploadFile(null);
    setUploadPreviewUrl(null);
    setError('');
    setMenuName('');
    setMenuType('Food');
    setMenuDesc('');
    setDishes([]);
    setSaving(false);
    setScrapedFiles([]);
  };

  useEffect(() => {
    if (!venue?.id) { setScrapedFiles([]); return; }
    const paths = [
      ...(venue.pdfMenuStoragePath ? [{ path: venue.pdfMenuStoragePath, isPdf: true }] : []),
      ...(venue.menuImageStoragePaths || []).map(p => ({ path: p, isPdf: false })),
    ];
    if (!paths.length) { setScrapedFiles([]); return; }
    Promise.all(
      paths.map(async ({ path, isPdf }) => {
        try {
          const url = await firebase.storage().ref(path).getDownloadURL();
          return { url, isPdf };
        } catch { return null; }
      })
    ).then(results => setScrapedFiles(results.filter(Boolean)));
  }, [venue?.id]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    if (file) {
      setUploadFile(file);
      setUploadPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Kick off scan from a stored URL (fetch → Blob → File → FormData)
  const handleScanUrl = async (url) => {
    setError('');
    setStep(1);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('Could not fetch the stored image URL');
      const blob = await res.blob();
      const ext = url.split('.').pop().split('?')[0] || 'jpg';
      const file = new File([blob], `menu.${ext}`, {
        type: blob.type || 'image/jpeg',
      });
      await runScan(file);
    } catch (err) {
      console.error('URL scan error:', err);
      setError(err.message || 'Failed to fetch image. Try uploading a file instead.');
      setStep(0);
    }
  };

  const handleScanUpload = () => {
    if (!uploadFile) return;
    setError('');
    setStep(1);
    runScan(uploadFile);
  };

  const runScan = async (file) => {
    try {
      const formData = new FormData();
      formData.append('menuFile', file);

      const response = await fetch(CV_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`CV service returned HTTP ${response.status}`);
      }

      const result = await response.json();
      const { menuName: mn, menuType: mt, items } = mergePageResults(
        result.jsonDataArray || []
      );

      setMenuName(mn || (venue?.bizName ? `${venue.bizName} Menu` : ''));
      setMenuType(mt || 'Food');
      setMenuDesc('');
      setDishes(
        items.map((item) => ({
          _id: uuidv4(),
          name: item.name || '',
          description: item.description || '',
          price: item.price || '',
          dietary: [
            ...(item.standard_features || []),
            ...(item.extras || []),
          ].join(', '),
        }))
      );
      setStep(2);
    } catch (err) {
      console.error('CV scan error:', err);
      setError(err.message || 'Failed to analyse image. Please try again.');
      setStep(0);
    }
  };

  const updateDish = (id, field, val) =>
    setDishes((ds) => ds.map((d) => (d._id === id ? { ...d, [field]: val } : d)));

  const removeDish = (id) =>
    setDishes((ds) => ds.filter((d) => d._id !== id));

  const addEmptyDish = () =>
    setDishes((ds) => [
      ...ds,
      { _id: uuidv4(), name: '', description: '', price: '', dietary: '' },
    ]);

  const handleSaveImport = async () => {
    if (!menuName.trim()) {
      setError('Menu name is required before saving.');
      return;
    }
    setSaving(true);
    try {
      const db = firebase.firestore();
      const menuID = uuidv4();
      const bizId = venue.id;

      await db.collection('MenuDetails').doc(menuID).set({
        menuId: menuID,
        menuBizId: bizId,
        menuName: menuName.trim(),
        menuType,
        menuDesc: menuDesc.trim(),
        menuOn: true,
        menuOnTime: '08:00',
        menuOffTime: '22:00',
        importedFromPhoto: true,
        importedByAdmin: true,
        createdOn: firebase.firestore.FieldValue.serverTimestamp(),
      });

      if (dishes.length > 0) {
        const batch = db.batch();
        dishes.forEach((dish) => {
          const dishRef = db.collection('DishDetails').doc();
          batch.set(dishRef, {
            dishName: dish.name,
            dishDesc: dish.description,
            dishMenuId: menuID,
            bizId,
            dishAvailSize: [
              {
                size: 'Standard',
                price: parseFloat(dish.price) || 0,
                id: uuidv4(),
              },
            ],
            dishOn: true,
            dishAvailFrom: '08:00',
            dishAvailTill: '22:00',
            importedFromPhoto: true,
            importedByAdmin: true,
            createdOn: firebase.firestore.FieldValue.serverTimestamp(),
            ...parseDietaryTags(dish.dietary),
          });
        });
        await batch.commit();
      }

      onImported?.();
      handleClose();
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save menu. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Images the venue already has stored (from import, menu scrape, or mobile submit)
  const venueImages = [
    ...(venue?.images || []),
    ...(venue?.menuImages || []),
    ...(venue?.bizPic1 ? [venue.bizPic1] : []),
    ...(Array.isArray(venue?.bizPic) ? venue.bizPic : []),
    ...(Array.isArray(venue?.bizMenuPhotos) ? venue.bizMenuPhotos : []),
  ].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={step === 1 ? undefined : handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome color="secondary" />
          Scan Menu with AI — {venue?.bizName}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* ── Step 0: Source selection ── */}
        {step === 0 && (
          <Box>
            {scrapedFiles.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Scraped menu files
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Downloaded from the venue website. Click to send to GPT-4o for extraction.
                </Typography>

                {/* PDF files */}
                {scrapedFiles.filter(f => f.isPdf).map((f, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                       p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <PictureAsPdf color="error" />
                    <Typography variant="body2" sx={{ flex: 1 }}>PDF Menu {i + 1}</Typography>
                    <Button size="small" variant="contained" color="secondary"
                            startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                            onClick={() => handleScanUrl(f.url)}>
                      Scan PDF
                    </Button>
                  </Box>
                ))}

                {/* Menu images */}
                {scrapedFiles.filter(f => !f.isPdf).length > 0 && (
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {scrapedFiles.filter(f => !f.isPdf).map((f, i) => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                          <img src={f.url} alt={`Menu image ${i + 1}`}
                               style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                          <Button size="small" variant="contained" color="secondary"
                                  startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                                  sx={{ position: 'absolute', bottom: 4, left: 4, right: 4,
                                        fontSize: '0.65rem', py: 0.25 }}
                                  onClick={() => handleScanUrl(f.url)}>
                            Scan
                          </Button>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
                <Divider sx={{ mt: 2 }} />
              </Box>
            )}

            {venueImages.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Scan from stored venue images
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Click an image below to send it directly to the GPT-4o vision model.
                </Typography>
                <Grid container spacing={1}>
                  {venueImages.slice(0, 8).map((url, idx) => (
                    <Grid item xs={6} sm={3} key={idx}>
                      <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                        <img
                          src={url}
                          alt={`Venue image ${idx + 1}`}
                          style={{
                            width: '100%',
                            height: 100,
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            left: 4,
                            right: 4,
                            fontSize: '0.65rem',
                            py: 0.25,
                          }}
                          onClick={() => handleScanUrl(url)}
                        >
                          Scan
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {venueImages.length > 0 ? 'or upload a different file' : 'Upload a menu file'}
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => fileRef.current?.click()}
              >
                Choose Photo or PDF
              </Button>
            </Box>

            {/* ── Upload file preview ───────────────────────────────────── */}
            {uploadFile && uploadPreviewUrl && (
              <Box
                sx={{
                  mt: 2,
                  border: '1px solid',
                  borderColor: 'secondary.light',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'secondary.50',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'secondary.dark', display: 'block', mb: 1 }}
                >
                  PREVIEW — verify this is the correct file before scanning
                </Typography>

                {uploadFile.type === 'application/pdf' ? (
                  <Box
                    component="embed"
                    src={uploadPreviewUrl}
                    type="application/pdf"
                    title="PDF preview"
                    sx={{ width: '100%', height: 420, borderRadius: 1 }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={uploadPreviewUrl}
                    alt="Menu image preview"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 420,
                      objectFit: 'contain',
                      display: 'block',
                      mx: 'auto',
                      borderRadius: 1,
                    }}
                  />
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {uploadFile.name} — {(uploadFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── Step 1: Analysing ── */}
        {step === 1 && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CircularProgress size={64} color="secondary" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Analysing menu...
            </Typography>
            <Typography color="text.secondary">
              GPT-4o is reading the image. This usually takes 10–30 seconds.
            </Typography>
          </Box>
        )}

        {/* ── Step 2: Review ── */}
        {step === 2 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Review the extracted data below and edit anything that looks wrong before saving.
            </Alert>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  label="Menu Name"
                  size="small"
                  value={menuName}
                  onChange={(e) => setMenuName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={menuType}
                    label="Type"
                    onChange={(e) => setMenuType(e.target.value)}
                  >
                    <MenuItem value="Food">Food</MenuItem>
                    <MenuItem value="Drinks">Drinks</MenuItem>
                    <MenuItem value="Desserts">Desserts</MenuItem>
                    <MenuItem value="Specials">Specials</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Description (optional)"
                  size="small"
                  value={menuDesc}
                  onChange={(e) => setMenuDesc(e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Dishes ({dishes.length} extracted)
            </Typography>

            <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
              {dishes.map((dish) => (
                <Box
                  key={dish._id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    mb: 1,
                  }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Dish Name"
                        value={dish.name}
                        onChange={(e) => updateDish(dish._id, 'name', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={dish.description}
                        onChange={(e) =>
                          updateDish(dish._id, 'description', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Price ($)"
                        value={dish.price}
                        onChange={(e) => updateDish(dish._id, 'price', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Dietary tags"
                        placeholder="GF, V, Vegan…"
                        value={dish.dietary}
                        onChange={(e) =>
                          updateDish(dish._id, 'dietary', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{ textAlign: 'center' }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeDish(dish._id)}
                        aria-label="Remove dish"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}

              {dishes.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No dishes were extracted. Add them manually or go back and try a different image.
                </Typography>
              )}
            </Box>

            <Button
              startIcon={<Add />}
              size="small"
              onClick={addEmptyDish}
              sx={{ mt: 1 }}
            >
              Add row
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            {uploadFile && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AutoAwesome />}
                onClick={handleScanUpload}
              >
                Analyse Menu
              </Button>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <Button startIcon={<ArrowBack />} onClick={() => setStep(0)}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
              onClick={handleSaveImport}
            >
              {saving
                ? 'Saving…'
                : `Import ${dishes.length} dish${dishes.length !== 1 ? 'es' : ''}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdminMenuCVImport;
